import React, { useEffect, useState } from "react";
import {
  Loader, Sparkles, RefreshCw, Trash2, Save, Send, Upload,
  FileText, ListChecks, Clock, Plus, RotateCcw, Image as ImageIcon,
} from "lucide-react";

const API = "https://api.growl.games/api/blog-auto";
const UPLOADS = "https://api.growl.games/uploads";

const authHeaders = (json = true) => {
  const token = localStorage.getItem("token") || "";
  return {
    ...(json ? { "Content-Type": "application/json" } : {}),
    Authorization: token ? `Bearer ${token}` : "",
  } as Record<string, string>;
};

interface PromptRow {
  id: number;
  name: string;
  category: string;
  prompt_text: string;
  variable_label: string;
  variable_mode: string;
  daily_variables: string;
  runs_per_day: number;
  enabled: boolean;
  auto_thumbnail: boolean;
}

interface DraftRow {
  id: number;
  prompt_name: string;
  category: string;
  variable: string;
  title: string;
  subheading: string;
  author: string;
  image: string | null;
  status: string;
  thumb_status: string | null;
  error: string | null;
  blog_id: number | null;
  created_at: string;
  html_len: number;
}

interface DraftFull extends DraftRow { html: string; }

const CATEGORIES = ["News", "Guides", "Casino Reviews", "iGaming News - UK", "iGaming News - USA", "iGaming News - India", "iGaming News - Global"];

const STATUS_COLORS: Record<string, string> = {
  queued: "bg-slate-100 text-slate-600",
  generating: "bg-blue-100 text-blue-700",
  draft: "bg-yellow-100 text-yellow-800",
  posted: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  discarded: "bg-gray-100 text-gray-500",
};

const BlogAutomation: React.FC = () => {
  const [tab, setTab] = useState<"drafts" | "prompts" | "schedule">("drafts");
  const [drafts, setDrafts] = useState<DraftRow[]>([]);
  const [showAllDrafts, setShowAllDrafts] = useState(false);
  const [openDraft, setOpenDraft] = useState<DraftFull | null>(null);
  const [prompts, setPrompts] = useState<PromptRow[]>([]);
  const [editPrompt, setEditPrompt] = useState<Partial<PromptRow> | null>(null);
  const [statusInfo, setStatusInfo] = useState<any>(null);
  const [thumbPrompt, setThumbPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [thumbBusy, setThumbBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [genVars, setGenVars] = useState<Record<number, string>>({});

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(""), 6000); };

  const loadDrafts = async (all = showAllDrafts) => {
    const r = await fetch(`${API}/drafts${all ? "?all=1" : ""}`, { headers: authHeaders() });
    const d = await r.json();
    if (d.status === "success") setDrafts(d.rows);
  };
  const loadPrompts = async () => {
    const r = await fetch(`${API}/prompts`, { headers: authHeaders() });
    const d = await r.json();
    if (d.status === "success") setPrompts(d.rows);
  };
  const loadStatus = async () => {
    const r = await fetch(`${API}/status`, { headers: authHeaders() });
    const d = await r.json();
    if (d.status === "success") {
      setStatusInfo(d);
      if (d.settings && typeof d.settings.thumbnail_prompt === "string") setThumbPrompt(d.settings.thumbnail_prompt);
    }
  };
  const loadAll = () => { loadDrafts(); loadPrompts(); loadStatus(); };

  useEffect(() => { loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // poll while generations are running
  useEffect(() => {
    const iv = setInterval(() => {
      if (statusInfo && statusInfo.queue_length > 0) { loadStatus(); loadDrafts(); }
    }, 12000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusInfo]);

  const api = async (path: string, body?: any, method = "POST") => {
    setBusy(true);
    try {
      const r = await fetch(`${API}${path}`, {
        method, headers: authHeaders(),
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
      const d = await r.json();
      if (!r.ok || d.status !== "success") throw new Error(d.message || `HTTP ${r.status}`);
      return d;
    } finally { setBusy(false); }
  };

  const openDraftFull = async (id: number) => {
    const r = await fetch(`${API}/drafts/${id}`, { headers: authHeaders() });
    const d = await r.json();
    if (d.status === "success") setOpenDraft(d.row);
  };

  const saveDraft = async () => {
    if (!openDraft) return;
    try {
      await api(`/drafts/${openDraft.id}`, {
        title: openDraft.title, subheading: openDraft.subheading, author: openDraft.author,
        html: openDraft.html, category: openDraft.category,
      });
      flash("Draft saved."); loadDrafts();
    } catch (e: any) { flash(`Save failed: ${e.message}`); }
  };

  const uploadThumb = async (file: File) => {
    if (!openDraft) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const r = await fetch(`${API}/drafts/${openDraft.id}/thumbnail`, {
        method: "POST", headers: authHeaders(false), body: fd,
      });
      const d = await r.json();
      if (d.status !== "success") throw new Error(d.message);
      setOpenDraft({ ...openDraft, image: d.image });
      flash("Thumbnail uploaded."); loadDrafts();
    } catch (e: any) { flash(`Upload failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  const genThumbnail = async () => {
    if (!openDraft) return;
    setThumbBusy(true);
    try {
      const r = await fetch(`${API}/drafts/${openDraft.id}/generate-thumbnail`, { method: "POST", headers: authHeaders() });
      const d = await r.json();
      if (!r.ok || d.status !== "success") throw new Error(d.message || `HTTP ${r.status}`);
      setOpenDraft({ ...openDraft, image: d.image });
      flash("Thumbnail generated via wavespeed.");
      loadDrafts();
    } catch (e: any) { flash(`Thumbnail failed: ${e.message}`); }
    finally { setThumbBusy(false); }
  };

  const postDraft = async () => {
    if (!openDraft) return;
    if (!window.confirm(`Post "${openDraft.title}" live to growl.games?`)) return;
    try {
      const d = await api(`/drafts/${openDraft.id}/post`);
      flash(`Posted! blog id ${d.blog_id}. Sitemap + bot snapshots refresh automatically in ~3 min.`);
      setOpenDraft(null); loadDrafts();
    } catch (e: any) { flash(`Post failed: ${e.message}`); }
  };

  const savePrompt = async () => {
    if (!editPrompt) return;
    try {
      if (editPrompt.id) await api(`/prompts/${editPrompt.id}`, editPrompt);
      else await api(`/prompts`, editPrompt);
      setEditPrompt(null); loadPrompts(); flash("Prompt saved.");
    } catch (e: any) { flash(`Prompt save failed: ${e.message}`); }
  };

  const generateNow = async (p: PromptRow) => {
    const raw = (genVars[p.id] || "").trim();
    const variables = raw ? raw.split("\n").map((s) => s.trim()).filter(Boolean) : [""];
    try {
      const d = await api(`/generate`, { prompt_id: p.id, variables });
      flash(`Queued ${d.draft_ids.length} generation(s). Each takes ~2-5 min.`);
      setTab("drafts"); loadDrafts(); loadStatus();
    } catch (e: any) { flash(`Generate failed: ${e.message}`); }
  };

  const tabBtn = (key: typeof tab, label: string, icon: React.ReactNode) => (
    <button
      onClick={() => setTab(key)}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-t-md text-sm font-semibold ${
        tab === key ? "bg-white text-blue-700 shadow" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
      }`}
    >{icon}{label}</button>
  );

  return (
    <div className="ml-0 lg:ml-[256px] p-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-xl font-bold text-gray-100 mb-1">Blog Automation</h1>
        <p className="text-sm text-gray-400 mb-4">
          Master prompts → Claude Sonnet 4.6 (Max plan, thinking off) → drafts → your review + thumbnail → Post.
          Nothing goes live without the Post button.
        </p>
        {msg && <div className="mb-3 text-sm bg-blue-50 border border-blue-200 text-blue-800 rounded px-3 py-2">{msg}</div>}
        {statusInfo && statusInfo.queue_length > 0 && (
          <div className="mb-3 text-sm bg-purple-50 border border-purple-200 text-purple-800 rounded px-3 py-2 flex items-center gap-2">
            <Loader size={14} className="animate-spin" />
            Generating… {statusInfo.queue_length} in queue. List refreshes automatically.
          </div>
        )}

        <div className="flex gap-1">
          {tabBtn("drafts", "Drafts", <FileText size={15} />)}
          {tabBtn("prompts", "Master Prompts", <ListChecks size={15} />)}
          {tabBtn("schedule", "Daily Schedule", <Clock size={15} />)}
        </div>

        {/* ================= DRAFTS ================= */}
        {tab === "drafts" && (
          <div className="bg-white rounded-b-lg rounded-tr-lg shadow p-4">
            <div className="flex items-center gap-3 mb-3">
              <label className="flex items-center gap-1 text-xs text-gray-500">
                <input type="checkbox" checked={showAllDrafts}
                  onChange={(e) => { setShowAllDrafts(e.target.checked); loadDrafts(e.target.checked); }} />
                show posted/discarded
              </label>
              <button onClick={() => { loadDrafts(); loadStatus(); }} className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                <RefreshCw size={12} /> Refresh
              </button>
              <div className="flex-1" />
              <button
                onClick={() => { if (window.confirm("Delete ALL failed and discarded drafts?")) api(`/drafts/clear-failed`).then((d) => { flash(`Deleted ${d.deleted} draft(s).`); loadDrafts(); }); }}
                className="flex items-center gap-1 text-xs text-red-500 hover:underline">
                <Trash2 size={12} /> Clear failed/discarded
              </button>
            </div>
            <div className="divide-y">
              {drafts.length === 0 && <div className="p-4 text-sm text-gray-400">No drafts. Generate from the Master Prompts tab.</div>}
              {drafts.map((d) => (
                <div key={d.id} className="py-2">
                  <div className="flex items-center gap-2 cursor-pointer" onClick={() => openDraftFull(d.id)}>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${STATUS_COLORS[d.status] || "bg-gray-100"}`}>
                      {d.status === "generating"
                        ? <span className="flex items-center gap-1"><Loader size={9} className="animate-spin" />gen</span>
                        : d.status === "queued"
                          ? "queued"
                          : d.status}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">{d.category}</span>
                    {d.image
                      ? <span className="text-[10px] text-green-600 font-semibold">🖼 thumb</span>
                      : d.thumb_status === "generating"
                        ? <span className="text-[10px] text-purple-600 font-semibold flex items-center gap-0.5"><Loader size={8} className="animate-spin" />thumb</span>
                        : d.thumb_status === "failed"
                          ? <span className="text-[10px] text-red-500 font-semibold">thumb ✕</span>
                          : null}
                    <span className="text-sm text-gray-800 flex-1 truncate">
                      {d.title || <i className="text-gray-400">({d.prompt_name}{d.variable ? ` · ${d.variable}` : ""})</i>}
                    </span>
                    <span className="text-[11px] text-gray-400">{new Date(d.created_at).toLocaleString()}</span>
                    {(d.status === "failed" || d.status === "discarded") && (
                      <button
                        onClick={(e) => { e.stopPropagation(); if (window.confirm("Delete this draft permanently?")) api(`/drafts/${d.id}/delete`).then(() => { flash("Draft deleted."); loadDrafts(); }); }}
                        className="flex items-center gap-1 text-[11px] text-red-500 hover:underline">
                        <Trash2 size={11} /> delete
                      </button>
                    )}
                  </div>
                  {d.status === "failed" && d.error && (
                    <div className="text-xs text-red-600 mt-1 flex items-center gap-2">
                      {d.error.slice(0, 160)}
                      <button onClick={() => api(`/drafts/${d.id}/retry`).then(() => { flash("Re-queued."); loadDrafts(); })}
                        className="flex items-center gap-1 text-blue-600 hover:underline"><RotateCcw size={11} />retry</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* draft editor modal */}
        {openDraft && (
          <div className="fixed inset-0 bg-black/50 z-[9998] flex items-start justify-center overflow-y-auto p-4" onClick={() => setOpenDraft(null)}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl p-5 mt-6 mb-10" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${STATUS_COLORS[openDraft.status]}`}>{openDraft.status}</span>
                <button onClick={() => setOpenDraft(null)} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
              </div>
              <div className="space-y-3">
                <input value={openDraft.title} onChange={(e) => setOpenDraft({ ...openDraft, title: e.target.value })}
                  placeholder="Headline" className="w-full border rounded px-3 py-2 text-sm font-semibold" />
                <input value={openDraft.subheading} onChange={(e) => setOpenDraft({ ...openDraft, subheading: e.target.value })}
                  placeholder="Sub-heading" className="w-full border rounded px-3 py-2 text-sm" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 mb-1">Author</label>
                    <input value={openDraft.author} onChange={(e) => setOpenDraft({ ...openDraft, author: e.target.value })}
                      placeholder="Author" className="w-full border rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 mb-1">Blog category</label>
                    <input value={openDraft.category} list="blogcats" onChange={(e) => setOpenDraft({ ...openDraft, category: e.target.value })}
                      placeholder="Category" className="w-full border rounded px-3 py-2 text-sm" />
                    <datalist id="blogcats">{CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
                  </div>
                </div>
                <details className="border rounded">
                  <summary className="px-3 py-2 text-xs font-semibold text-gray-600 cursor-pointer">Edit HTML source</summary>
                  <textarea value={openDraft.html} onChange={(e) => setOpenDraft({ ...openDraft, html: e.target.value })}
                    rows={14} className="w-full border-t px-3 py-2 text-xs font-mono" />
                </details>
                <div className="border rounded p-3 max-h-[380px] overflow-y-auto bg-gray-50">
                  <div className="text-[10px] font-bold text-gray-400 uppercase mb-2">Preview</div>
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: openDraft.html }} />
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-md text-xs font-semibold cursor-pointer">
                    <Upload size={13} />
                    {openDraft.image ? "Replace thumbnail" : "Upload thumbnail *"}
                    <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden"
                      onChange={(e) => e.target.files && e.target.files[0] && uploadThumb(e.target.files[0])} />
                  </label>
                  <button
                    onClick={genThumbnail}
                    disabled={busy || thumbBusy}
                    title="Generate a 16:9 thumbnail with wavespeed using the News Thumbnail Master Prompt + this headline"
                    className="flex items-center gap-2 bg-purple-100 hover:bg-purple-200 disabled:opacity-60 text-purple-700 px-3 py-2 rounded-md text-xs font-semibold">
                    {thumbBusy ? <Loader size={13} className="animate-spin" /> : <ImageIcon size={13} />}
                    {thumbBusy ? "Generating…" : "Auto-thumbnail"}
                  </button>
                  {openDraft.image && (
                    <img src={`${UPLOADS}/${openDraft.image}`} alt="thumbnail" className="h-14 rounded border" />
                  )}
                  <div className="flex-1" />
                  <button onClick={saveDraft} disabled={busy}
                    className="flex items-center gap-1 bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-md text-xs font-semibold">
                    <Save size={13} /> Save
                  </button>
                  {openDraft.status === "draft" && (
                    <>
                      <button onClick={() => api(`/drafts/${openDraft.id}/discard`).then(() => { setOpenDraft(null); loadDrafts(); })}
                        className="flex items-center gap-1 bg-red-50 hover:bg-red-100 text-red-600 px-4 py-2 rounded-md text-xs font-semibold">
                        <Trash2 size={13} /> Discard
                      </button>
                      <button onClick={postDraft} disabled={busy || !openDraft.image}
                        title={!openDraft.image ? "Upload a thumbnail first" : ""}
                        className="flex items-center gap-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-5 py-2 rounded-md text-xs font-bold">
                        <Send size={13} /> POST TO GROWL.GAMES
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ================= PROMPTS ================= */}
        {tab === "prompts" && (
          <div className="bg-white rounded-b-lg rounded-tr-lg shadow p-4 space-y-3">
            <button onClick={() => setEditPrompt({ variable_mode: "rotate", category: "News", runs_per_day: 1, enabled: true })}
              className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-xs font-semibold">
              <Plus size={14} /> Add master prompt
            </button>
            {prompts.map((p) => (
              <div key={p.id} className="border rounded-lg p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`w-2 h-2 rounded-full ${p.enabled ? "bg-green-500" : "bg-gray-300"}`} />
                  <span className="font-semibold text-sm text-gray-800">{p.name}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">{p.category}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                    {p.variable_mode === "queue" ? `queue · ${p.runs_per_day}/day` : "rotate daily"}
                  </span>
                  <span className="text-[11px] text-gray-400 flex-1">
                    {p.variable_label && `input: ${p.variable_label}`}
                  </span>
                  <button onClick={() => setEditPrompt({ ...p })} className="text-xs text-blue-600 hover:underline">edit</button>
                  <button onClick={() => { if (window.confirm(`Delete prompt "${p.name}"?`)) api(`/prompts/${p.id}/delete`).then(loadPrompts); }}
                    className="text-xs text-red-500 hover:underline">delete</button>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <textarea
                    value={genVars[p.id] || ""}
                    onChange={(e) => setGenVars({ ...genVars, [p.id]: e.target.value })}
                    placeholder={p.variable_label ? `${p.variable_label} — one per line (blank = single run)` : "input value(s), one per line (optional)"}
                    rows={1}
                    className="flex-1 border rounded px-3 py-1.5 text-xs"
                  />
                  <button onClick={() => generateNow(p)} disabled={busy}
                    className="flex items-center gap-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white px-4 py-1.5 rounded-md text-xs font-semibold">
                    <Sparkles size={13} /> Generate now
                  </button>
                </div>
              </div>
            ))}
            {prompts.length === 0 && <div className="text-sm text-gray-400 p-3">No master prompts yet — add your News / Guides / Casino Reviews prompts.</div>}
          </div>
        )}

        {/* prompt editor modal */}
        {editPrompt && (
          <div className="fixed inset-0 bg-black/50 z-[9998] flex items-start justify-center overflow-y-auto p-4" onClick={() => setEditPrompt(null)}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-5 mt-6 mb-10" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-bold text-gray-800 mb-3">{editPrompt.id ? "Edit master prompt" : "New master prompt"}</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 mb-1">Prompt name (for your reference)</label>
                    <input value={editPrompt.name || ""} onChange={(e) => setEditPrompt({ ...editPrompt, name: e.target.value })}
                      placeholder="e.g. News / Guides / Casino Reviews" className="w-full border rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 mb-1">Blog category — where posts get filed (type or pick)</label>
                    <input value={editPrompt.category || ""} list="blogcats2" onChange={(e) => setEditPrompt({ ...editPrompt, category: e.target.value })}
                      placeholder="News / Guides / Casino Reviews..." className="w-full border rounded px-3 py-2 text-sm" />
                    <datalist id="blogcats2">{CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 mb-1">Input label — what this prompt needs per run</label>
                    <input value={editPrompt.variable_label || ""} onChange={(e) => setEditPrompt({ ...editPrompt, variable_label: e.target.value })}
                      placeholder="e.g. GEO / Topic / Casino name" className="w-full border rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 mb-1">Daily scheduling mode</label>
                    <div className="flex items-center gap-2">
                      <select value={editPrompt.variable_mode || "rotate"} onChange={(e) => setEditPrompt({ ...editPrompt, variable_mode: e.target.value })}
                        className="border rounded px-3 py-2 text-sm bg-white flex-1">
                        <option value="rotate">rotate — every value, every day (e.g. US/UK/INDIA/EU)</option>
                        <option value="queue">queue — consume N per day (topics / casino names)</option>
                      </select>
                      {editPrompt.variable_mode === "queue" && (
                        <input type="number" min={1} value={editPrompt.runs_per_day || 1}
                          onChange={(e) => setEditPrompt({ ...editPrompt, runs_per_day: parseInt(e.target.value, 10) || 1 })}
                          className="border rounded px-2 py-2 text-sm w-16" title="articles per day" />
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 mb-1">Daily input values — one per line (rotate: each value daily · queue: backlog consumed top-down)</label>
                  <textarea value={editPrompt.daily_variables || ""} onChange={(e) => setEditPrompt({ ...editPrompt, daily_variables: e.target.value })}
                    placeholder={"rotate example:\nUS\nUK\nINDIA\nEU\nqueue example: one topic or casino name per line"}
                    rows={3} className="w-full border rounded px-3 py-2 text-xs" />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 mb-1">Master prompt (stored verbatim — never modified by the system)</label>
                  <textarea value={editPrompt.prompt_text || ""} onChange={(e) => setEditPrompt({ ...editPrompt, prompt_text: e.target.value })}
                    placeholder={"Paste the MASTER PROMPT here, verbatim.\nOptional: put {{VARIABLE}} where the input (GEO/topic/casino) should be inserted; otherwise it is appended at the end as 'INPUT FOR THIS RUN: ...'"}
                    rows={14} className="w-full border rounded px-3 py-2 text-xs font-mono" />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input type="checkbox" checked={editPrompt.enabled !== false}
                    onChange={(e) => setEditPrompt({ ...editPrompt, enabled: e.target.checked })} />
                  include in the daily schedule
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input type="checkbox" checked={!!editPrompt.auto_thumbnail}
                    onChange={(e) => setEditPrompt({ ...editPrompt, auto_thumbnail: e.target.checked })} />
                  auto-generate thumbnail via wavespeed (uses the News Thumbnail Master Prompt + headline)
                </label>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditPrompt(null)} className="px-4 py-2 rounded-md text-xs bg-gray-100 hover:bg-gray-200 text-gray-600">Cancel</button>
                  <button onClick={savePrompt} disabled={busy || !editPrompt.name || !editPrompt.prompt_text}
                    className="flex items-center gap-1 px-5 py-2 rounded-md text-xs font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white">
                    <Save size={13} /> Save prompt
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ================= SCHEDULE ================= */}
        {tab === "schedule" && statusInfo && (
          <div className="bg-white rounded-b-lg rounded-tr-lg shadow p-4 space-y-5 max-w-2xl">
            <div className={`rounded-md px-3 py-2 text-sm font-semibold ${statusInfo.settings.daily_enabled ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-500"}`}>
              {statusInfo.settings.daily_enabled
                ? `⚠ Auto-generation is ON — the system will generate blogs by itself every day at ${statusInfo.settings.daily_time} (server/UTC time), even when you're away.`
                : "Auto-generation is OFF. Blogs are only generated when you click Generate or Run the daily plan. Nothing runs on its own."}
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 font-semibold">
                <input type="checkbox" checked={!!statusInfo.settings.daily_enabled}
                  onChange={(e) => {
                    if (e.target.checked && !window.confirm("Turn ON automatic daily generation?\n\nThe system will generate all scheduled blogs by itself every day at the set time — even when you are away from your PC — and this uses your Claude Max plan quota. Only enable this if you want unattended daily generation.")) return;
                    api(`/settings`, { daily_enabled: e.target.checked, daily_time: statusInfo.settings.daily_time }).then(loadStatus);
                  }} />
                Generate automatically every day at
              </label>
              <input type="time" value={statusInfo.settings.daily_time || "06:00"}
                onChange={(e) => api(`/settings`, { daily_enabled: statusInfo.settings.daily_enabled, daily_time: e.target.value }).then(loadStatus)}
                className="border rounded px-2 py-1 text-sm" />
              <span className="text-xs text-gray-400">(server/UTC time)</span>
            </div>
            <button onClick={() => api(`/run-daily-plan`).then((d) => { flash(`Daily plan started: ${d.queued} generations queued.${d.notes.length ? " " + d.notes.join("; ") : ""}`); setTab("drafts"); loadDrafts(); loadStatus(); })}
              disabled={busy}
              className="flex items-center gap-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white px-4 py-2 rounded-md text-xs font-semibold">
              <Sparkles size={14} /> Run the daily plan now
            </button>
            <div className="text-xs text-gray-500 space-y-1">
              <div>Last auto-run date: {statusInfo.settings.last_run_date || "never"}</div>
              <div>Draft counts: {Object.entries(statusInfo.counts || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || "none"}</div>
              <div className="pt-2 text-gray-400">
                The daily plan runs every enabled master prompt: "rotate" prompts generate one article per
                listed value (news GEOs), "queue" prompts consume N values per day from their backlog
                (guide topics / casino names). Generated articles land in Drafts and wait for your review —
                nothing is posted automatically.
              </div>
            </div>

            {/* News Thumbnail Master Prompt (wavespeed) */}
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-bold text-gray-700">News Thumbnail Master Prompt</h3>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusInfo.wavespeed_configured ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                  {statusInfo.wavespeed_configured ? "wavespeed key set" : "wavespeed key missing"}
                </span>
              </div>
              <p className="text-[11px] text-gray-400 mb-2">
                Used by wavespeed.ai (Seedream, 16:9) to auto-make thumbnails for prompts with
                "auto-generate thumbnail" enabled. The article headline is appended automatically —
                or put <code>{"{{HEADLINE}}"}</code> where you want it inserted.
              </p>
              <textarea
                value={thumbPrompt}
                onChange={(e) => setThumbPrompt(e.target.value)}
                rows={6}
                placeholder="Paste your News Thumbnail Master Prompt here..."
                className="w-full border rounded px-3 py-2 text-xs font-mono"
              />
              <button
                onClick={() => api(`/settings`, { daily_enabled: statusInfo.settings.daily_enabled, daily_time: statusInfo.settings.daily_time, thumbnail_prompt: thumbPrompt }).then(() => { flash("Thumbnail prompt saved."); loadStatus(); })}
                disabled={busy}
                className="mt-2 flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-4 py-1.5 rounded-md text-xs font-semibold">
                <Save size={13} /> Save thumbnail prompt
              </button>
              {!statusInfo.wavespeed_configured && (
                <p className="text-[11px] text-red-500 mt-2">
                  Add <code>WAVESPEED_API_KEY</code> to backend/.env and run <code>pm2 restart backend</code> to enable auto-thumbnails.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogAutomation;
